/**
 * Resource Details Screen
 * 
 * Full details view for a single resource with download and view capabilities.
 * Accessible to both managers and sales reps.
 * 
 * @module ResourceDetailsScreen
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Timestamp } from 'firebase/firestore';
import {
  ArrowLeft,
  Download,
  Eye,
  Calendar,
  User,
  Tag,
  FileText,
  Video,
  Image as ImageIcon,
  Link as LinkIcon,
  ExternalLink,
  Star,
  X,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { ResourceService } from '../../services/ResourceService';
import { useAuth } from '../../contexts/AuthContext';
import { Resource, ResourceType, FileMetadata } from '../../@types/resource';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';
import { formatTextForDisplay } from '../../utils/formatText';

/**
 * Styled Components
 */
const Header = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  background: white;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: ${Spacing.lg};

  &:hover {
    background: ${ThemeColors.background.cards};
    border-color: ${ThemeColors.primary.main};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Hero = styled.div<{ type: ResourceType; hasMedia: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 100%;
  background: ${props =>
    props.hasMedia
      ? ThemeColors.background.cards
      : (() => {
    switch (props.type) {
            case 'document':
              return `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`;
            case 'video':
              return `linear-gradient(135deg, #f093fb 0%, #f5576c 100%)`;
            case 'photo':
              return `linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)`;
            case 'link':
              return `linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)`;
            case 'text':
              return `linear-gradient(135deg, #fa709a 0%, #fee140 100%)`;
            default:
              return `linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark})`;
    }
        })()};
  border-radius: ${BorderRadius.xl};
  position: relative;
  overflow: hidden;
  box-shadow: ${Shadows.xl};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
  pointer-events: auto;
  cursor: default;

  &::before {
    content: ${props => (props.hasMedia ? 'none' : "''")};
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 50%, rgba(255,255,255,0.15) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const HeroMediaWrapper = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  border-radius: ${BorderRadius.lg};
  overflow: hidden;
  background: ${ThemeColors.background.header};
  box-shadow: ${Shadows.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  pointer-events: auto;

  video,
  iframe {
    width: 100%;
    height: 100%;
    border: none;
    pointer-events: auto;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: ${ThemeColors.background.paper};
`;

const VideoPlayer = styled.video`
  width: 100%;
  height: 100%;
  background: black;
`;

const PdfViewer = styled.iframe`
  width: 100%;
  height: 100%;
  background: white;
`;

const MediaThumbnails = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  overflow-x: auto;
  padding-top: ${Spacing.sm};
  z-index: 2;
`;

const ThumbnailButton = styled.button.withConfig({
  shouldForwardProp: (prop) => {
    if (typeof prop !== 'string') {
      return true;
    }
    const propName = prop as string;
    return propName !== 'active' && propName !== 'imageUrl';
  },
})<{ active: boolean; imageUrl?: string }>`
  width: 72px;
  height: 56px;
  border-radius: ${BorderRadius.md};
  border: ${props =>
    props.active ? `2px solid ${ThemeColors.primary.main}` : `1px solid ${ThemeColors.border.light}`};
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  background: ${props => (props.imageUrl ? `url(${props.imageUrl}) center/cover no-repeat` : ThemeColors.background.paper)};
  position: relative;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${ThemeColors.focus.ring};
  }
`;

const HeroIcon = styled.div`
  width: 120px;
  height: 120px;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  align-self: center;
  z-index: 2;

  svg {
    width: 60px;
    height: 60px;
  }
`;

const HeroControls = styled.div`
  position: absolute;
  top: ${Spacing.xl};
  left: ${Spacing.xl};
  display: flex;
  gap: ${Spacing.sm};
  z-index: 3;

  @media (max-width: 768px) {
    top: ${Spacing.lg};
    left: ${Spacing.lg};
  }
`;

const PreviewTriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.lg};
  border-radius: ${BorderRadius.lg};
  border: none;
  background: rgba(15, 23, 42, 0.6);
  color: white;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  backdrop-filter: blur(10px);
  box-shadow: ${Shadows.md};
  transition: all 0.2s ease;

  &:hover {
    background: rgba(15, 23, 42, 0.8);
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xxl};
  z-index: 1000;

  @media (max-width: 768px) {
    padding: ${Spacing.lg};
  }
`;

const PreviewDialog = styled.div<{ fullscreen?: boolean }>`
  position: relative;
  width: ${props => props.fullscreen ? '100%' : 'min(1100px, 100%)'};
  height: ${props => props.fullscreen ? '100vh' : 'min(90vh, 880px)'};
  max-height: ${props => props.fullscreen ? '100vh' : 'min(90vh, 880px)'};
  background: ${ThemeColors.background.paper};
  border-radius: ${props => props.fullscreen ? '0' : BorderRadius.xl};
  box-shadow: ${Shadows.xl};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.md};
  padding: ${Spacing.lg} ${Spacing.xl};
  border-bottom: 1px solid ${ThemeColors.border.light};
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: ${Spacing.md} ${Spacing.lg};
  }
`;

const PreviewHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const PreviewTitle = styled.h2`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const ClosePreviewButton = styled.button`
  border: none;
  background: transparent;
  color: ${ThemeColors.text.secondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xs};
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${ThemeColors.text.primary};
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const PreviewBody = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${ThemeColors.background.paper};
  overflow: auto;
  padding: ${Spacing.xl};
  min-height: 0;

  @media (max-width: 768px) {
    padding: ${Spacing.md};
  }
`;

const PreviewFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${Spacing.md};
  padding: ${Spacing.lg} ${Spacing.xl};
  border-top: 1px solid ${ThemeColors.border.light};
  background: ${ThemeColors.background.cards};
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: ${Spacing.sm};
  }
`;

const PreviewActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.xl};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  border: ${props =>
    props.variant === 'secondary'
      ? `1px solid ${ThemeColors.border.dark}`
      : 'none'};
  background: ${props =>
    props.variant === 'secondary'
      ? ThemeColors.background.paper
      : ThemeColors.primary.main};
  color: ${props =>
    props.variant === 'secondary'
      ? ThemeColors.text.primary
      : 'white'};
  box-shadow: ${Shadows.sm};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${Shadows.md};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  max-height: 100%;
  background: black;
`;

const PreviewPdf = styled.iframe`
  width: 100%;
  min-height: 800px;
  height: 100%;
  border: none;
  background: white;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  width: auto;
  height: auto;
  max-height: none;
  object-fit: contain;
  border-radius: ${BorderRadius.lg};
  box-shadow: ${Shadows.md};
`;

const FeaturedBanner = styled.div`
  position: absolute;
  top: ${Spacing.xl};
  right: ${Spacing.xl};
  background: ${ThemeColors.warning.main};
  color: white;
  padding: ${Spacing.sm} ${Spacing.lg};
  border-radius: ${BorderRadius.lg};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.bold};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  box-shadow: ${Shadows.lg};
  z-index: 2;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacing.xl};
  margin-bottom: ${Spacing.xl};
  align-items: flex-start;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const ContentSection = styled.div`
  background: white;
  border-radius: ${BorderRadius.xl};
  padding: ${Spacing.xxl};
  box-shadow: ${Shadows.md};
  flex: 1;
  min-width: 0;
`;

const PreviewCard = styled.div`
  flex: 1;
  min-width: 0;
  position: sticky;
  top: ${Spacing.xl};
  align-self: flex-start;
  height: 90vh;
  min-height: 600px;
  max-height: 90vh;
  cursor: default;

  @media (max-width: 1024px) {
    position: relative;
    top: 0;
    height: 90vh;
    min-height: 500px;
    max-height: 90vh;
  }
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.md} 0;
  line-height: 1.2;
`;

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
  padding-bottom: ${Spacing.xl};
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};

  svg {
    width: 18px;
    height: 18px;
    color: ${ThemeColors.primary.main};
  }

  strong {
    color: ${ThemeColors.text.primary};
    font-weight: ${Typography.fontWeight.semiBold};
  }
`;

const Description = styled.div`
  font-size: ${Typography.fontSize.md};
  line-height: 1.7;
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xl};
  overflow-wrap: break-word;
  word-break: normal;

  p {
    margin: 0 0 0.75em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 0 0 0.75em;
    padding-left: 1.5em;
  }

  li {
    margin-bottom: 0.25em;
  }

  a {
    color: ${ThemeColors.primary.main};
    text-decoration: underline;
    font-weight: ${Typography.fontWeight.medium};
    word-break: normal;
    overflow-wrap: normal;

    &:hover {
      color: ${ThemeColors.primary.dark};
    }
  }

  strong,
  b {
    font-weight: ${Typography.fontWeight.bold};
  }
`;

const TextContent = styled.div`
  background: ${ThemeColors.background.cards};
  padding: ${Spacing.xl};
  border-radius: ${BorderRadius.lg};
  border-left: 4px solid ${ThemeColors.primary.main};
  font-size: ${Typography.fontSize.md};
  line-height: 1.8;
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xl};
  white-space: pre-wrap;
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.xl};
`;

const TagBadge = styled.span`
  padding: ${Spacing.sm} ${Spacing.md};
  background: ${ThemeColors.primary.light};
  color: ${ThemeColors.primary.dark};
  border-radius: ${BorderRadius.lg};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const FilesSection = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const FileCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
  align-items: flex-start;
  padding: ${Spacing.lg};
  background: white;
  border: 2px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.lg};
  margin-bottom: ${Spacing.md};
  cursor: default;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
    transform: translateY(-2px);
  }
`;

const FileActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
  width: 100%;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.md};
  width: 100%;
`;

const FileIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: ${ThemeColors.primary.light};
  color: ${ThemeColors.primary.main};
  border-radius: ${BorderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const FileDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const FileName = styled.div`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xs};
`;

const FileSize = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.lg};
  background: ${ThemeColors.primary.main};
  color: white;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  border: none;
  cursor: pointer;
  box-shadow: ${Shadows.sm};
  transition: all 0.2s ease;
  width: 100%;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${ThemeColors.primary.dark};
  }
`;

const OpenInNewTabButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.lg};
  background: white;
  color: ${ThemeColors.text.primary};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  box-shadow: ${Shadows.sm};
  transition: all 0.2s ease;
  width: 100%;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${ThemeColors.background.secondary};
    border-color: ${ThemeColors.primary.main};
    color: ${ThemeColors.primary.main};
  }
`;

const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md} ${Spacing.xl};
  background: linear-gradient(135deg, ${ThemeColors.success.main}, ${ThemeColors.success.dark});
  color: white;
  border-radius: ${BorderRadius.lg};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: ${Shadows.md};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${Shadows.lg};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xxl};
  font-size: ${Typography.fontSize.lg};
  color: ${ThemeColors.text.secondary};
`;

const ErrorState = styled.div`
  padding: ${Spacing.xl};
  background: ${ThemeColors.error.light};
  color: ${ThemeColors.error.dark};
  border-radius: ${BorderRadius.lg};
  text-align: center;
`;

/**
 * Get resource icon
 */
const getResourceIcon = (type: ResourceType) => {
  switch (type) {
    case 'document': return <FileText />;
    case 'video': return <Video />;
    case 'photo': return <ImageIcon />;
    case 'link': return <LinkIcon />;
    case 'text': return <FileText />;
    default: return <FileText />;
  }
};

/**
 * Format file size
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const getImageUrls = (resource: Resource): string[] => {
  const urls = new Set<string>();
  if (resource.thumbnailUrl) {
    urls.add(resource.thumbnailUrl);
  }
  if (resource.photoUrls && resource.photoUrls.length > 0) {
    resource.photoUrls.forEach(url => urls.add(url));
  }
  if (resource.files && resource.files.length > 0) {
    resource.files
      .filter(file => file.type?.startsWith('image/'))
      .forEach(file => urls.add(file.url));
  }
  return Array.from(urls);
};

const getVideoUrl = (resource: Resource): string | null => {
  if (resource.videoUrl) {
    return resource.videoUrl;
  }
  const videoFile = resource.files?.find(file => file.type?.startsWith('video/'));
  return videoFile?.url || null;
};

const getPdfFile = (resource: Resource): FileMetadata | undefined => {
  return resource.files?.find(
    file =>
      file.type?.toLowerCase().includes('pdf') ||
      file.name?.toLowerCase().endsWith('.pdf')
  );
};

const toDate = (value: Date | Timestamp): Date => {
  if (value instanceof Date) {
    return value;
  }
  if (typeof (value as Timestamp).toDate === 'function') {
    return (value as Timestamp).toDate();
  }
  return new Date(value as any);
};

/**
 * ResourceDetailsScreen Component
 */
export const ResourceDetailsScreen: React.FC = () => {
  const { resourceId } = useParams<{ resourceId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const [resource, setResource] = useState<Resource | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Ensure preview is closed when resource changes
  useEffect(() => {
    setIsPreviewOpen(false);
    setIsFullscreen(false);
  }, [resourceId]);

  /**
   * Fetch resource details
   */
  useEffect(() => {
    const fetchResource = async () => {
      if (!resourceId) {
        setError('No resource ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const resourceData = await ResourceService.getResourceById(resourceId);
        
        if (!resourceData) {
          setError('Resource not found');
          return;
        }

        setResource(resourceData);

        // Track view
        if (user?.id) {
          await ResourceService.trackView(
            resourceId,
          user.id,
          user.username || user.email || 'Unknown',
          user.role === 'manager' ? 'manager' : 'sales-representative'
          );
        }
      } catch (err) {
        console.error('Error fetching resource:', err);
        setError('Failed to load resource');
      } finally {
        setIsLoading(false);
      }
    };

    fetchResource();
  }, [resourceId, user]);

  /**
   * Handle file download
   */
  const handleDownload = async (fileUrl: string, fileName: string) => {
    if (resource && user?.id) {
      await ResourceService.trackDownload(
        resource.id,
        user.id,
        user.username || user.email || 'Unknown',
        user.role === 'manager' ? 'manager' : 'sales-representative',
        fileUrl,
        fileName
      );
    }
    window.open(fileUrl, '_blank');
  };

  /**
   * Handle back
   */
  const handleBack = () => {
    navigate(-1);
  };

  const categoryInfo = resource ? ResourceService.getCategoryInfo(resource.category) : undefined;
  const imageUrls = useMemo(() => (resource ? getImageUrls(resource) : []), [resource]);
  const videoUrl = useMemo(() => (resource ? getVideoUrl(resource) : null), [resource]);
  const pdfFile = useMemo(() => (resource ? getPdfFile(resource) : undefined), [resource]);
  const primaryDownloadFile = useMemo(() => {
    if (!resource) {
      return null;
    }

    if (pdfFile) {
      return {
        url: pdfFile.url,
        name: pdfFile.name
      };
    }

    if (videoUrl) {
      const matchedVideo = resource.files?.find(file => file.url === videoUrl);
      if (matchedVideo) {
        return {
          url: matchedVideo.url,
          name: matchedVideo.name
        };
      }
    }

    if (selectedImage) {
      const matchedImage = resource.files?.find(file => file.url === selectedImage);
      if (matchedImage) {
        return {
          url: matchedImage.url,
          name: matchedImage.name
        };
      }

      if (resource.thumbnailUrl === selectedImage || resource.photoUrls?.includes(selectedImage)) {
        return {
          url: selectedImage,
          name: `${resource.title}.jpg`
        };
      }
    }

    return null;
  }, [pdfFile, resource, selectedImage, videoUrl]);

  useEffect(() => {
    if (imageUrls.length > 0) {
      setSelectedImage(imageUrls[0]);
    } else {
      setSelectedImage(null);
    }
  }, [imageUrls]);

  useEffect(() => {
    if (!isPreviewOpen) {
      document.body.style.overflow = '';
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsPreviewOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPreviewOpen]);

  const hasPrimaryMedia = Boolean(videoUrl || pdfFile || selectedImage);
  const primaryMediaUrl = videoUrl || pdfFile?.url || selectedImage || null;
  const openPreview = () => {
    if (hasPrimaryMedia) {
      setIsPreviewOpen(true);
    }
  };
  const closePreview = () => {
    setIsPreviewOpen(false);
    setIsFullscreen(false);
  };
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  const openMediaInNewTab = () => {
    if (!primaryMediaUrl) return;
    window.open(primaryMediaUrl, '_blank', 'noopener,noreferrer');
  };
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking directly on the overlay, not on the dialog or its children
    if (event.target === event.currentTarget) {
      closePreview();
    }
  };

  const handleDialogClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Prevent clicks inside the dialog from closing the preview
    event.stopPropagation();
  };

  if (isLoading) {
    return (
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <LoadingState>{t('resources.loading', 'Loading resource...')}</LoadingState>
        </MainContent>
      </PageContainer>
    );
  }

  if (error || !resource) {
    return (
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <ErrorState>
            <h2>{error || 'Resource not found'}</h2>
            <BackButton onClick={handleBack} style={{ marginTop: Spacing.lg }}>
              <ArrowLeft />
              {t('common.back', 'Go Back')}
            </BackButton>
          </ErrorState>
        </MainContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
        <Header>
          <BackButton onClick={handleBack}>
            <ArrowLeft />
            {t('resources.backToResources', 'Back to Resources')}
          </BackButton>
        </Header>

        {/* Cards Container */}
        <CardsContainer>
          {/* Resource Details Card */}
          <ContentSection>
          <Title>{resource.title}</Title>

          <MetaInfo>
            <MetaItem>
              {categoryInfo?.icon} <strong>{categoryInfo?.name}</strong>
            </MetaItem>
            <MetaItem>
              <User />
              {t('resources.createdBy', 'Created by')} <strong>{resource.createdByName}</strong>
            </MetaItem>
            <MetaItem>
              <Calendar />
              {toDate(resource.createdAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </MetaItem>
            <MetaItem>
              <Eye />
              <strong>{resource.viewCount || 0}</strong> {t('resources.views', 'views')}
            </MetaItem>
            <MetaItem>
              <Download />
              <strong>{resource.downloadCount || 0}</strong> {t('resources.downloads', 'downloads')}
            </MetaItem>
          </MetaInfo>

          {/* Description */}
          <Description
            dangerouslySetInnerHTML={{
              __html: formatTextForDisplay(resource.description),
            }}
          />

          {/* Text Content */}
          {resource.textContent && (
            <>
              <SectionTitle>
                <FileText />
                {t('resources.content', 'Content')}
              </SectionTitle>
              <TextContent>{resource.textContent}</TextContent>
            </>
          )}

          {/* Link */}
          {resource.linkUrl && (
            <>
              <SectionTitle>
                <LinkIcon />
                {t('resources.externalLink', 'External Link')}
              </SectionTitle>
              <LinkButton href={resource.linkUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink />
                {t('resources.openLink', 'Open Link')}
              </LinkButton>
            </>
          )}

          {/* Files */}
          {resource.files && resource.files.length > 0 && (
            <FilesSection>
              <SectionTitle>
                <Download />
                {t('resources.files', 'Files')} ({resource.files.length})
              </SectionTitle>
              {resource.files.map((file, index) => (
                <FileCard key={index}>
                  <FileInfo>
                    <FileIconWrapper>
                      {getResourceIcon(resource.type)}
                    </FileIconWrapper>
                    <FileDetails>
                      <FileName>{file.name}</FileName>
                      <FileSize>{formatFileSize(file.size)}</FileSize>
                    </FileDetails>
                  </FileInfo>
                  <FileActions>
                    <DownloadButton
                      type="button"
                      onClick={() => handleDownload(file.url, file.name)}
                    >
                      <Download />
                      {t('common.download', 'Download')}
                    </DownloadButton>
                    <OpenInNewTabButton
                      type="button"
                      onClick={() => window.open(file.url, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink />
                      {t('resources.openInNewTab', 'Open in new tab')}
                    </OpenInNewTabButton>
                  </FileActions>
                </FileCard>
              ))}
            </FilesSection>
          )}

          {/* Tags */}
          {resource.tags && resource.tags.length > 0 && (
            <>
              <SectionTitle>
                <Tag />
                {t('resources.tags', 'Tags')}
              </SectionTitle>
              <TagsList>
                {resource.tags.map(tag => (
                  <TagBadge key={tag}>
                    <Tag />
                    {tag}
                  </TagBadge>
                ))}
              </TagsList>
            </>
          )}
          </ContentSection>

          {/* Document/Media Preview Card */}
          <PreviewCard>
            <Hero type={resource.type} hasMedia={hasPrimaryMedia}>
              {hasPrimaryMedia && (
                <HeroControls>
                  <PreviewTriggerButton type="button" onClick={openPreview}>
                    <Eye />
                    {t('resources.openPreview', 'Open preview')}
                  </PreviewTriggerButton>
                </HeroControls>
              )}
              {resource.featured && (
                <FeaturedBanner>
                  <Star />
                  {t('resources.featuredResource', 'Featured Resource')}
                </FeaturedBanner>
              )}
              {videoUrl ? (
                <HeroMediaWrapper>
                  <VideoPlayer
                    controls
                    src={videoUrl}
                    poster={selectedImage || undefined}
                  />
                </HeroMediaWrapper>
              ) : pdfFile ? (
                <HeroMediaWrapper>
                  <PdfViewer
                    src={`${pdfFile.url}#toolbar=0&navpanes=0`}
                    title={pdfFile.name}
                    aria-label={pdfFile.name}
                  />
                </HeroMediaWrapper>
              ) : selectedImage ? (
                <>
                  <HeroMediaWrapper>
                    <HeroImage src={selectedImage} alt={resource.title} />
                  </HeroMediaWrapper>
                  {imageUrls.length > 1 && (
                    <MediaThumbnails>
                      {imageUrls.map(url => (
                        <ThumbnailButton
                          key={url}
                          type="button"
                          active={selectedImage === url}
                          imageUrl={url}
                          onClick={() => setSelectedImage(url)}
                          aria-label={t('resources.previewImage', 'Preview image')}
                        />
                      ))}
                    </MediaThumbnails>
                  )}
                </>
              ) : (
              <HeroIcon>
                {getResourceIcon(resource.type)}
              </HeroIcon>
              )}
            </Hero>
          </PreviewCard>
        </CardsContainer>
        {isPreviewOpen && (
          <PreviewOverlay
            role="dialog"
            aria-modal="true"
            aria-label={t('resources.mediaPreview', 'Resource media preview')}
            onClick={handleOverlayClick}
          >
            <PreviewDialog fullscreen={isFullscreen} onClick={handleDialogClick}>
              <PreviewHeader>
                <PreviewTitle>{resource.title}</PreviewTitle>
                <PreviewHeaderActions>
                  <PreviewActionButton
                    type="button"
                    variant="secondary"
                    onClick={toggleFullscreen}
                    aria-label={isFullscreen ? t('common.exitFullscreen', 'Exit fullscreen') : t('common.fullscreen', 'Fullscreen')}
                  >
                    {isFullscreen ? <Minimize2 /> : <Maximize2 />}
                    {isFullscreen ? t('common.exitFullscreen', 'Exit fullscreen') : t('common.fullscreen', 'Fullscreen')}
                  </PreviewActionButton>
                  <ClosePreviewButton
                    type="button"
                    onClick={closePreview}
                    aria-label={t('common.close', 'Close')}
                  >
                    <X />
                  </ClosePreviewButton>
                </PreviewHeaderActions>
              </PreviewHeader>
              <PreviewBody>
                {videoUrl ? (
                  <PreviewVideo
                    controls
                    autoPlay
                    src={videoUrl}
                    poster={selectedImage || undefined}
                  />
                ) : pdfFile ? (
                  <PreviewPdf
                    src={`${pdfFile.url}#view=FitH`}
                    title={pdfFile.name}
                  />
                ) : selectedImage ? (
                  <PreviewImage src={selectedImage} alt={resource.title} />
                ) : null}
              </PreviewBody>
              {(primaryMediaUrl || primaryDownloadFile) && (
                <PreviewFooter>
                  {primaryMediaUrl && (
                    <PreviewActionButton
                      type="button"
                      variant="secondary"
                      onClick={openMediaInNewTab}
                    >
                      <ExternalLink />
                      {t('resources.openInNewTab', 'Open in new tab')}
                    </PreviewActionButton>
                  )}
                  {primaryDownloadFile && (
                    <PreviewActionButton
                      type="button"
                      onClick={() =>
                        handleDownload(primaryDownloadFile.url, primaryDownloadFile.name)
                      }
                    >
                      <Download />
                      {t('common.download', 'Download')}
                    </PreviewActionButton>
                  )}
                </PreviewFooter>
              )}
            </PreviewDialog>
          </PreviewOverlay>
        )}
      </MainContent>
    </PageContainer>
  );
};

